import React from "react";

type ModalVariant = "error" | "success" | "warning" | "info";

interface ModalButton {
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary" | "danger";
    autoFocus?: boolean;
}

interface ActionModalProps {
    open: boolean;
    variant?: ModalVariant;
    title?: string;
    message: string | React.ReactNode;
    primaryButton?: ModalButton;   // button on the right
    secondaryButton?: ModalButton; // optional button on the left
    onClose?: () => void;          // fallback when no buttons provided
    showCloseIcon?: boolean;
}

const variantConfig: Record<
    ModalVariant,
    { headerBg: string; iconColor: string; defaultTitle: string; icon: React.ReactNode }
> = {
    error: {
        headerBg: "bg-red-600",
        iconColor: "text-red-600",
        defaultTitle: "Ocurrió un error",
        icon: (
            <svg
                className="mt-0.5 h-7 w-7 shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9z" />
            </svg>
        ),
    },
    success: {
        headerBg: "bg-green-600",
        iconColor: "text-green-600",
        defaultTitle: "Operación exitosa",
        icon: (
            <svg className="mt-0.5 h-7 w-7 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
        ),
    },
    warning: {
        headerBg: "bg-yellow-500",
        iconColor: "text-yellow-500",
        defaultTitle: "Advertencia",
        icon: (
            <svg className="mt-0.5 h-7 w-7 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01" />
            </svg>
        ),
    },
    info: {
        headerBg: "bg-primary",
        iconColor: "text-primary",
        defaultTitle: "Información",
        icon: (
            <svg className="mt-0.5 h-7 w-7 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
            </svg>
        ),
    },
};

const ActionModal: React.FC<ActionModalProps> = ({
    open,
    variant = "error",
    title,
    message,
    primaryButton,
    secondaryButton,
    onClose,
    showCloseIcon = false,
}) => {
    if (!open) return null;

    const cfg = variantConfig[variant];
    const headerTitle = title ?? cfg.defaultTitle;

    const handleDefaultClose = () => {
        onClose?.();
    };

    const renderPrimaryClass = (btn?: ModalButton) => {
        if (btn?.variant === "danger") return "rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition";
        if (btn?.variant === "secondary") return "rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition";
        // default: primary
        if (variant === "success") return "rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition";
        if (variant === "warning") return "rounded-lg bg-yellow-500 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-600 transition";
        return "rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition";
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-lg animate-fadeIn">

                {/* Header */}
                <div className={`${cfg.headerBg} px-5 py-3 flex items-center justify-between`}>
                    <h2 className="text-base font-semibold text-white">{headerTitle}</h2>
                    {showCloseIcon && (
                        <button onClick={handleDefaultClose} className="text-white/90 hover:text-white/100">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="px-8 py-5">
                    <div className="flex items-start gap-4">
                        <div className={`${cfg.iconColor}`}>
                            {cfg.icon}
                        </div>

                        <div className="text-sm text-gray-700 leading-relaxed">
                            {message}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 pb-5">
                    {secondaryButton ? (
                        <button
                            onClick={secondaryButton.onClick}
                            className={
                                secondaryButton.variant === "secondary"
                                    ? "rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition"
                                    : "rounded-lg bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                            }
                        >
                            {secondaryButton.label}
                        </button>
                    ) : null}

                    {primaryButton ? (
                        <button
                            onClick={primaryButton.onClick}
                            className={renderPrimaryClass(primaryButton)}
                            autoFocus={primaryButton.autoFocus}
                        >
                            {primaryButton.label}
                        </button>
                    ) : onClose ? (
                        <button
                            onClick={handleDefaultClose}
                            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition"
                        >
                            Entendido
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default ActionModal;